import { useBlocker, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { RunArtifact } from "~/api/opsTypes";
import {
  useApproveRun,
  useControlRun,
  useFlowDefinition,
  useRetryRun,
  useRunArtifacts,
  useRunDetail,
  useRunLogs,
  useRunSteps,
  useRunTree,
  useSetBreakpoints,
  useStepRun,
  useStopRun,
} from "~/api/queries";
import { ABILITY } from "~/api/types";
import { ActionLedger } from "~/features/automation/ActionLedger";
import { BandStrip, Pill, StatusPill } from "~/features/automation/components";
import { formatDuration } from "~/features/automation/format";
import { runKindLabel } from "~/features/automation/status";
import { ArtifactViewer } from "~/features/runs/ArtifactViewer";
import { BreakpointRow } from "~/features/runs/BreakpointRow";
import { JumpDialog } from "~/features/runs/JumpDialog";
import { PublishDialog } from "~/features/runs/PublishDialog";
import { ReasoningRunTab } from "~/features/runs/ReasoningRunTab";
import { RunArtifactsTab } from "~/features/runs/RunArtifactsTab";
import { RunCanvas } from "~/features/runs/RunCanvas";
import { RunControls } from "~/features/runs/RunControls";
import { RunLogsTab } from "~/features/runs/RunLogsTab";
import { RunResultTab } from "~/features/runs/RunResultTab";
import { RunStepsTab } from "~/features/runs/RunStepsTab";
import {
  clockTime,
  elapsedMs,
  graphCarriesWords,
  isLive,
  readGraph,
} from "~/features/runs/runDerive";
import { SteeringPanel } from "~/features/runs/SteeringPanel";
import { Button } from "~/foundation/components/Button";
import { Dialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { Input } from "~/foundation/components/Input";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { cn } from "~/foundation/utils/cn";

/** The viewer's tabs; `?tab=` on the route opens one directly (a link from the research trace lands on "reasoning"). */
export type RunTab = "steps" | "logs" | "artifacts" | "result" | "reasoning";
type TabId = RunTab;

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-sm px-3 py-1.5 text-body",
        active ? "bg-bg-secondary font-semibold text-text" : "text-text-secondary",
      )}
    >
      {children}
    </button>
  );
}

/**
 * The `/runs/$runId` scene — the run viewer.
 *
 * A route rather than a modal, deliberately: this is the surface an operator sits on for
 * minutes while a run moves, it hosts a full-height canvas, and it keeps a navigation stack
 * for child runs. A modal cannot be linked or reloaded, and in kms it had to break out of its
 * own container with `position: fixed` to stop the canvas rendering blank.
 *
 * Everything that writes is gated by the run's own `can` block, which the server computes. The
 * viewer never re-derives a permission and never infers one from the status: a control that
 * reaches no executor reports success while the run keeps spending.
 */
export function RunPage({
  runId,
  definition: pinnedDefinition,
  initialTab,
}: {
  runId: string;
  definition?: unknown;
  /** The tab to open first; a reasoning-kind run still lands on "reasoning". */
  initialTab?: RunTab;
}) {
  const { t } = useTranslation();

  // The child-run back-stack. `runId` is the route's run; anything pushed here (a child, or a
  // parent reached through the causal crumb) is browsed without leaving the page.
  const [stack, setStack] = useState<string[]>([]);
  useEffect(() => setStack([]), []);
  const activeRunId = stack.at(-1) ?? runId;

  const navigate = useNavigate();
  const [reasoningDraftDirty, setReasoningDraftDirty] = useState(false);
  const [pendingViewChange, setPendingViewChange] = useState<(() => void) | null>(null);
  const draftBlocker = useBlocker({
    shouldBlockFn: () => reasoningDraftDirty,
    enableBeforeUnload: reasoningDraftDirty,
    withResolver: true,
  });
  function changeView(action: () => void) {
    if (reasoningDraftDirty) setPendingViewChange(() => action);
    else action();
  }
  const { data: detail, isLoading, isError } = useRunDetail(activeRunId);
  const run = detail?.run;
  const live = run ? isLive(run.status) : false;

  const { data: steps } = useRunSteps(activeRunId, live);
  // Weave (Docit Work 15 §6.1): what each node IS — the executor kind and the tile it carries.
  const { data: tree } = useRunTree(activeRunId, live);
  const { data: logs } = useRunLogs(activeRunId, live);
  const { data: artifacts } = useRunArtifacts(activeRunId, live);

  const control = useControlRun();
  const stepRun = useStepRun();
  const stopRun = useStopRun();
  const retryRun = useRetryRun();
  const approve = useApproveRun();
  const setBreakpoints = useSetBreakpoints();

  const [tab, setTab] = useState<TabId>(initialTab ?? "steps");
  useEffect(() => {
    if (activeRunId && run?.kind.toLowerCase() === "reasoning") setTab("reasoning");
  }, [activeRunId, run?.kind]);
  const [canvasMode, setCanvasMode] = useState<"canvas" | "graphs">("canvas");
  const [breakpointsOpen, setBreakpointsOpen] = useState(false);
  const [steeringOpen, setSteeringOpen] = useState(false);
  const [breakpoints, setLocalBreakpoints] = useState<string[]>([]);
  const [jumpOpen, setJumpOpen] = useState(false);
  const [stopOpen, setStopOpen] = useState(false);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [publishFor, setPublishFor] = useState<{ open: boolean; artifactPath: string | null }>({
    open: false,
    artifactPath: null,
  });
  const [viewing, setViewing] = useState<{ path: string; version: number } | null>(null);
  const [directive, setDirective] = useState("");

  // The elapsed clock. One tick a second while the run is live and not a beat after it ends —
  // a settled run's duration is a fact the server already measured.
  const [now, setNow] = useState(() => Date.now());
  const tick = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!live) {
      return;
    }
    tick.current = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      if (tick.current) {
        clearInterval(tick.current);
      }
    };
  }, [live]);

  const graph = useMemo(() => readGraph(detail?.graph ?? null), [detail?.graph]);
  // The canvas's pills show each step's prompt. A snapshot that is the definition AS RUN already
  // carries them; otherwise read the stored flow the tree names (its head version — the tree does
  // not say which version ran, so a since-edited prompt can differ; the step's as-sent prompt in
  // its meta is the exact one).
  const definitionSlug =
    !pinnedDefinition && !graphCarriesWords(graph) ? (tree?.flowSlug ?? "") : "";
  const { data: storedFlow } = useFlowDefinition(definitionSlug);

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }
  if (isError || !detail || !run) {
    return <Empty message={t("This run could not be found.")} />;
  }

  // Older run responses may omit aiFlowSlug; keep the steering control conditional.
  const slugCandidate = (run as unknown as { aiFlowSlug?: unknown }).aiFlowSlug;
  const flowSlug = typeof slugCandidate === "string" && slugCandidate ? slugCandidate : null;

  const busy =
    control.isPending ||
    stepRun.isPending ||
    stopRun.isPending ||
    retryRun.isPending ||
    setBreakpoints.isPending;

  const elapsed = elapsedMs(run, now);
  const allSteps = steps ?? [];
  const allLogs = logs ?? [];
  const allArtifacts = artifacts ?? [];

  const jumpNodes = allSteps
    .slice()
    .sort((a, b) => a.index - b.index)
    .map((step) => ({ id: step.nodeId, name: step.name || step.nodeId }));

  const openArtifact = (artifact: Pick<RunArtifact, "path" | "version">) =>
    setViewing({ path: artifact.path, version: artifact.version });

  /** Held in a const so the approval bar's two buttons can name the node without re-narrowing. */
  const approvalNodeId = run.awaitingApprovalNodeId;

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-4 p-6">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3">
        {stack.length > 0 ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => changeView(() => setStack((current) => current.slice(0, -1)))}
          >
            <span aria-hidden className="rtl:rotate-180">
              ←
            </span>
            {t("Back")}
          </Button>
        ) : null}

        <Text variant="h2">{run.name}</Text>
        <Pill>{runKindLabel(run.kind, t)}</Pill>
        <span className="inline-flex items-center gap-1.5">
          {live ? (
            <span
              aria-hidden
              className="inline-block h-2 w-2 animate-pulse rounded-full bg-accent motion-reduce:animate-none"
            />
          ) : null}
          <StatusPill status={run.status} />
        </span>
        <span className="text-small text-text-tertiary tabular-nums">
          {formatDuration(elapsed, t)}
        </span>

        {detail.causalRunId ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              changeView(() => setStack((current) => [...current, detail.causalRunId as string]))
            }
          >
            <span aria-hidden className="rtl:rotate-180">
              ↥
            </span>
            {t("Parent run {{id}}", { id: detail.causalRunId.slice(0, 8) })}
          </Button>
        ) : null}

        {detail.host ? (
          <span className="text-small text-text-tertiary">
            {detail.host === "ide" ? t("Ran in an editor") : t("Ran on the server")}
          </span>
        ) : null}
      </div>

      {detail.statusReason ? <Text variant="small">{detail.statusReason}</Text> : null}

      {/* ── Control strip ───────────────────────────────────────────────── */}
      <RunControls
        run={run}
        breakpointCount={breakpoints.length}
        hasGraph={graph !== null}
        breakpointsOpen={breakpointsOpen}
        steeringOpen={steeringOpen}
        flowSlug={flowSlug}
        canvasMode={canvasMode}
        busy={busy}
        onPause={() => control.mutate({ id: activeRunId, action: "pause" })}
        onResume={() => control.mutate({ id: activeRunId, action: "resume" })}
        onStep={() =>
          stepRun.mutate(
            { id: activeRunId },
            {
              onSuccess: (ack) =>
                toast.success(
                  ack.stepMode
                    ? t("Step mode on — the run stops before each node.")
                    : t("Released one node."),
                ),
            },
          )
        }
        onJump={() => setJumpOpen(true)}
        onStop={() => setStopOpen(true)}
        onRetry={() =>
          retryRun.mutate(activeRunId, {
            onSuccess: (ack) => {
              toast.success(t("Started again."));
              if (ack.runId && ack.runId !== activeRunId) {
                changeView(() => setStack((current) => [...current, ack.runId]));
              }
            },
          })
        }
        onToggleBreakpoints={() => setBreakpointsOpen((value) => !value)}
        onToggleSteering={() => setSteeringOpen((value) => !value)}
        onToggleCanvasMode={() =>
          setCanvasMode((mode) => (mode === "canvas" ? "graphs" : "canvas"))
        }
        onOpenLedger={() => setLedgerOpen(true)}
      />

      {/* ── Approval bar ────────────────────────────────────────────────── */}
      {approvalNodeId ? (
        <BandStrip level="warn">
          <div className="flex flex-col gap-2">
            <span className="text-body font-semibold text-text">{t("Approval required")}</span>
            <span className="text-small text-text-secondary">
              {t(
                "The run is held before “{{node}}”. It is waiting for a human decision — no debugger control can release it.",
                { node: approvalNodeId },
              )}
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={directive}
                aria-label={t("Correction for the node")}
                placeholder={t("Optional correction, sent with your answer")}
                onChange={(event) => setDirective(event.target.value)}
              />
              <Button
                size="sm"
                disabled={run.abilities[ABILITY.runApprove] !== true || approve.isPending}
                onClick={() =>
                  approve.mutate(
                    {
                      id: activeRunId,
                      decision: {
                        approved: true,
                        nodeId: approvalNodeId,
                        ...(directive.trim() ? { directive: directive.trim() } : {}),
                      },
                    },
                    { onSuccess: () => setDirective("") },
                  )
                }
              >
                <span aria-hidden>✓</span>
                {t("Approve")}
              </Button>
              <Button
                size="sm"
                variant="danger"
                disabled={run.abilities[ABILITY.runApprove] !== true || approve.isPending}
                onClick={() =>
                  approve.mutate(
                    {
                      id: activeRunId,
                      decision: {
                        approved: false,
                        nodeId: approvalNodeId,
                        ...(directive.trim() ? { directive: directive.trim() } : {}),
                      },
                    },
                    { onSuccess: () => setDirective("") },
                  )
                }
              >
                <span aria-hidden>✕</span>
                {t("Deny")}
              </Button>
            </div>
          </div>
        </BandStrip>
      ) : null}

      {/* ── Degraded bar ────────────────────────────────────────────────── */}
      {run.degraded.length > 0 ? (
        <BandStrip level="warn">
          <div className="flex flex-col gap-1.5">
            <span className="text-body font-semibold text-text">
              {t("Answered without {{count}} declared source(s)", { count: run.degraded.length })}
            </span>
            <span className="text-small text-text-secondary">
              {t(
                "These inputs did not resolve. Nodes downstream of them were told so in their prompt and asked not to substitute recalled or estimated values.",
              )}
            </span>
            <ul className="flex flex-col gap-0.5">
              {run.degraded.map((entry) => (
                <li key={`${entry.nodeId}-${entry.sourceId}`} className="text-small text-text">
                  <span className="font-semibold">{entry.sourceId}</span>{" "}
                  <span className="text-text-tertiary">({entry.kind})</span> → {entry.nodeId}:{" "}
                  {entry.reason}
                </li>
              ))}
            </ul>
          </div>
        </BandStrip>
      ) : null}

      {/* ── Breakpoints and steering ────────────────────────────────────── */}
      {breakpointsOpen && graph ? (
        <BreakpointRow
          nodes={graph.nodes}
          active={breakpoints}
          busy={setBreakpoints.isPending}
          onChange={(nodeIds) =>
            setBreakpoints.mutate(
              { id: activeRunId, nodeIds },
              { onSuccess: (saved) => setLocalBreakpoints(saved) },
            )
          }
        />
      ) : null}
      {steeringOpen && flowSlug ? <SteeringPanel flowId={flowSlug} /> : null}

      {/* ── The graph ───────────────────────────────────────────────────── */}
      <RunCanvas
        mode={canvasMode}
        graph={graph}
        steps={allSteps}
        artifacts={allArtifacts}
        currentNodeId={run.currentNodeId}
        runName={run.name}
        tree={tree ?? null}
        definition={pinnedDefinition ?? storedFlow?.definition ?? null}
        onOpenTile={(key) => void navigate({ to: "/work/$key", params: { key } })}
        onReadArtifact={(path) => {
          const match = allArtifacts.find((artifact) => artifact.path === path);
          setViewing({ path, version: match?.version ?? 1 });
        }}
      />

      {/* ── Child runs ──────────────────────────────────────────────────── */}
      {detail.children.length > 0 ? (
        <section className="flex flex-col gap-2">
          <Text variant="h3">{t("Child runs")}</Text>
          <ul className="flex flex-col rounded-md border border-divider">
            {detail.children.map((child) => (
              <li key={child.runId} className="border-b border-divider last:border-b-0">
                <button
                  type="button"
                  onClick={() => changeView(() => setStack((current) => [...current, child.runId]))}
                  className="flex w-full items-center gap-3 px-3 py-2.5 text-start hover:bg-hover-bg"
                >
                  <StatusPill status={child.status} />
                  <span className="min-w-0 flex-1 truncate text-body text-text">
                    {child.name || child.runId.slice(0, 8)}
                  </span>
                  {child.aiFlowSlug ? (
                    <span className="text-small text-text-tertiary">{child.aiFlowSlug}</span>
                  ) : null}
                  <span className="text-small text-text-tertiary">
                    {child.via === "subflow" ? t("via subflow") : t("via amend")}
                  </span>
                  <span className="text-small text-text-secondary tabular-nums">
                    {child.nodesTotal > 0
                      ? `${child.nodesDone}/${child.nodesTotal}`
                      : (child.currentNodeId ?? "—")}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* ── Tabs ────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2 border-b border-divider pb-2">
        <TabButton active={tab === "steps"} onClick={() => changeView(() => setTab("steps"))}>
          {t("Steps")} · {allSteps.length}
        </TabButton>
        <TabButton active={tab === "logs"} onClick={() => changeView(() => setTab("logs"))}>
          {t("Logs")} · {allLogs.length}
        </TabButton>
        <TabButton
          active={tab === "artifacts"}
          onClick={() => changeView(() => setTab("artifacts"))}
        >
          {t("Artifacts")} · {allArtifacts.length}
        </TabButton>
        <TabButton active={tab === "result"} onClick={() => changeView(() => setTab("result"))}>
          {t("Result")}
        </TabButton>
        <TabButton active={tab === "reasoning"} onClick={() => setTab("reasoning")}>
          Reasoning
        </TabButton>
        {live ? (
          <span className="text-small text-text-tertiary">
            {t("Live · last read {{time}}", { time: clockTime(new Date(now).toISOString()) })}
          </span>
        ) : null}
      </div>

      {tab === "steps" ? <RunStepsTab steps={allSteps} /> : null}
      {tab === "logs" ? <RunLogsTab lines={allLogs} /> : null}
      {tab === "artifacts" ? (
        <RunArtifactsTab
          artifacts={allArtifacts}
          onView={openArtifact}
          onPublish={(artifact) => setPublishFor({ open: true, artifactPath: artifact.path })}
        />
      ) : null}
      {tab === "result" ? (
        <RunResultTab
          run={run}
          steps={allSteps}
          onPublish={() => setPublishFor({ open: true, artifactPath: null })}
        />
      ) : null}
      {tab === "reasoning" ? (
        <ReasoningRunTab key={run.id} run={run} onDraftDirtyChange={setReasoningDraftDirty} />
      ) : null}

      <Dialog
        open={draftBlocker.status === "blocked" || pendingViewChange !== null}
        onOpenChange={(open) => {
          if (!open) {
            draftBlocker.reset?.();
            setPendingViewChange(null);
          }
        }}
        title="Unsaved reasoning draft"
        closeLabel="Close draft dialog"
      >
        <p>Your draft or unresolved save will be lost if you leave this view.</p>
        <div className="mt-3 flex gap-2">
          <Button
            onClick={() => {
              draftBlocker.reset?.();
              setPendingViewChange(null);
            }}
          >
            Keep editing draft
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              setReasoningDraftDirty(false);
              pendingViewChange?.();
              setPendingViewChange(null);
              draftBlocker.proceed?.();
            }}
          >
            Discard draft and leave
          </Button>
        </div>
      </Dialog>

      {/* ── Dialogs ─────────────────────────────────────────────────────── */}
      <JumpDialog
        runId={activeRunId}
        open={jumpOpen}
        onClose={() => setJumpOpen(false)}
        currentNodeId={run.currentNodeId}
        nodes={jumpNodes}
      />

      <PublishDialog
        runId={activeRunId}
        open={publishFor.open}
        artifactPath={publishFor.artifactPath}
        onClose={() => setPublishFor({ open: false, artifactPath: null })}
      />

      <ArtifactViewer
        runId={activeRunId}
        path={viewing?.path ?? null}
        version={viewing?.version}
        onClose={() => setViewing(null)}
      />

      <ActionLedger runId={activeRunId} open={ledgerOpen} onClose={() => setLedgerOpen(false)} />

      {/* Two verbs, never one ambiguous button. Cooperative cancellation only takes effect at
          the next node boundary — during one such boundary a run kept spending ~1.4M tokens an
          hour after somebody pressed "stop". */}
      <Dialog
        open={stopOpen}
        onOpenChange={(next) => !next && setStopOpen(false)}
        title={t("Stop this run?")}
        description={t("It will be cancelled at the next safe point.")}
        closeLabel={t("Close")}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setStopOpen(false)}>
              {t("Cancel")}
            </Button>
            <Button
              variant="neutral"
              disabled={stopRun.isPending}
              onClick={() =>
                stopRun.mutate(
                  { id: activeRunId, mode: "graceful" },
                  {
                    onSuccess: (ack) => {
                      setStopOpen(false);
                      toast.success(ack.executorNote || t("Will stop at the next safe point."));
                    },
                  },
                )
              }
            >
              {t("Stop after this node")}
            </Button>
            <Button
              variant="danger"
              disabled={stopRun.isPending}
              onClick={() =>
                stopRun.mutate(
                  { id: activeRunId, mode: "immediate" },
                  {
                    onSuccess: () => {
                      setStopOpen(false);
                      toast.success(t("Cancelled immediately."));
                    },
                  },
                )
              }
            >
              {t("Abort now")}
            </Button>
          </>
        }
      >
        <p className="text-small text-text-secondary">
          {t(
            "Stopping after the current node is cheap and safe, but the node runs to completion — that can be another node's worth of tokens. Aborting now makes the server's state true immediately.",
          )}
        </p>
      </Dialog>
    </div>
  );
}
